/**
 * Category hub copy for `/betting-gaming` — demand signals, Ghana context, hub links.
 * Layout is rendered in `BettingGamingCategoryPageSections`; slugs must exist in BETTING_GAMING_ITEMS.
 */

export const BETTING_GAMING_DEMAND_INTRO = {
  kicker: "Ghana demand map",
  title: "Top 10 most searched topics under Betting & Gaming",
  subtitle:
    "Grounded in how Ghanaians actually bet today: football and mobile Money rails, crash games like Aviator, regulated operators, and the growth of online casinos — with room for responsible play and Gaming Commission context.",
};

/** Short labels for the “prioritize first” strip (links to hubs). */
export const BETTING_GAMING_PRIORITY_LINKS = [
  { label: "Sports betting", slug: "sports-betting-tips" },
  { label: "Aviator & crash", slug: "best-betting-platforms" },
  { label: "Casino games", slug: "casino-games" },
  { label: "Virtual sports", slug: "virtual-games" },
  { label: "Bonuses & promos", slug: "bonuses-promotions" },
  { label: "Mobile & MoMo", slug: "mobile-betting-apps" },
  { label: "Tips & predictions", slug: "odds-comparison-strategy" },
];

export const BETTING_GAMING_DEMAND_TOPICS = [
  {
    rank: 1,
    title: "Sports betting (football)",
    slug: "sports-betting-tips",
    why: "Football betting is the largest category in Ghana — EPL, UCL, La Liga, and the Ghana Premier League drive most of the volume.",
    questions: [
      "Which betting site is best in Ghana?",
      "How do I win football bets consistently?",
      "What is over/under betting?",
      "How do accumulators work?",
      "What are correct score predictions?",
    ],
    contentIdeas: [
      "Daily football predictions",
      "Betting tips",
      "Match analysis",
      "Odds comparison",
      "Beginner betting guides",
    ],
  },
  {
    rank: 2,
    title: "Aviator & crash games",
    slug: "best-betting-platforms",
    why: "Aviator-style crash games are among the fastest-growing products — users obsess over multipliers, cash-out timing, and platform choice.",
    questions: [
      "How does Aviator work?",
      "Is Aviator real or fake?",
      "Best Aviator betting apps?",
      "How do I win Aviator?",
      "Are Aviator signals real?",
    ],
    contentIdeas: [
      "Aviator beginner guides",
      "Responsible gambling tips",
      "Platform comparisons",
      "Live strategy discussions",
    ],
  },
  {
    rank: 3,
    title: "Mobile betting & MoMo",
    slug: "mobile-betting-apps",
    why: "Smartphone penetration plus MTN MoMo and Vodafone Cash make mobile the default — speed and data use matter as much as odds.",
    questions: [
      "Which betting apps support MoMo?",
      "Fastest withdrawal betting site?",
      "How do I deposit with MTN MoMo?",
      "Which betting app uses low data?",
    ],
    contentIdeas: [
      "Mobile betting tutorials",
      "Best betting apps",
      "MoMo deposit guides",
      "Fast withdrawal comparisons",
    ],
  },
  {
    rank: 4,
    title: "Online casino games",
    slug: "casino-games",
    why: "Slots, blackjack, roulette, and poker are pulling new audiences as licensed online casino offers scale up.",
    questions: [
      "Best online casino in Ghana?",
      "Are online casinos legal?",
      "Which slots pay most?",
      "How do casino bonuses work?",
    ],
    contentIdeas: [
      "Casino reviews",
      "Slot game guides",
      "Bonus explanations",
      "Responsible gambling content",
    ],
  },
  {
    rank: 5,
    title: "Betting bonuses & promotions",
    slug: "bonuses-promotions",
    why: "High intent: welcome offers, free bets, cashback, and acca boosts are actively compared before users commit.",
    questions: [
      "Which betting site has the biggest bonus?",
      "What is rollover?",
      "How do free bets work?",
      "Best welcome bonus in Ghana?",
    ],
    contentIdeas: [
      "Bonus comparison tables",
      "Promo breakdowns",
      "Free bet guides",
      "Terms & conditions explained",
    ],
  },
  {
    rank: 6,
    title: "Betting tips, predictions & odds",
    slug: "odds-comparison-strategy",
    why: "Daily picks, “sure odds” searches, and value-betting education remain huge — quality beats noise when trust is scarce.",
    questions: [
      "Today’s sure odds?",
      "Best betting tips?",
      "How do odds work?",
      "What is value betting?",
    ],
    contentIdeas: [
      "Daily prediction pages",
      "Odds education",
      "Expert analysis",
      "Betting strategy explainers",
    ],
  },
  {
    rank: 7,
    title: "Gambling laws & responsible gaming",
    slug: "responsible-gambling",
    why: "Readers want clarity on legality, age limits, tax, and addiction — Ghana’s Gaming Commission is the regulatory anchor.",
    questions: [
      "Is betting legal in Ghana?",
      "What is the legal betting age?",
      "Is betting taxed?",
      "How do I gamble responsibly?",
    ],
    contentIdeas: [
      "Gaming laws explained",
      "Responsible betting guides",
      "Addiction awareness",
      "Safe betting practices",
    ],
  },
  {
    rank: 8,
    title: "Virtual sports betting",
    slug: "virtual-games",
    why: "Virtual football and racing run 24/7 with fast results — ideal for mobile sessions that want instant feedback.",
    questions: [
      "How do virtual games work?",
      "Can you win virtual betting?",
      "Best virtual football strategy?",
    ],
    contentIdeas: [
      "Virtual sports tutorials",
      "Winning strategies",
      "Platform comparisons",
    ],
  },
  {
    rank: 9,
    title: "eSports betting & gaming",
    slug: "esports-betting",
    why: "Younger bettors follow FIFA, eFootball, CoD, and wider eSports markets as operators add fixtures.",
    questions: [
      "Can I bet on eSports?",
      "Best eSports betting sites?",
      "How does FIFA betting work?",
    ],
    contentIdeas: [
      "eSports betting guides",
      "Tournament coverage",
      "Gaming news",
    ],
  },
  {
    rank: 10,
    title: "Payments, withdrawals & accounts",
    slug: "payment-methods",
    why: "Trust lives in payout speed, failed deposits, MoMo quirks, and verification — make the rails legible.",
    questions: [
      "Which betting site pays fastest?",
      "Why is my withdrawal delayed?",
      "How do I verify my account?",
      "Betting withdrawal limits?",
    ],
    contentIdeas: [
      "Payment troubleshooting",
      "Withdrawal guides",
      "Verification tutorials",
      "Safe payment methods",
    ],
  },
];

export const BETTING_GAMING_BONUS_TOPICS = [
  { label: "Jackpot & accumulator betting", slug: "sports-betting-tips" },
  { label: "Live / in-play betting", slug: "sports-betting-tips" },
  { label: "Online casino games", slug: "casino-games" },
  { label: "Virtual sports / 24-7 games", slug: "virtual-games" },
  { label: "Betting psychology & addiction", slug: "responsible-gambling" },
  { label: "Crypto betting", slug: "payment-methods" },
  { label: "Strategies for beginners", slug: "odds-comparison-strategy" },
];

export const BETTING_GAMING_STRATEGIC = {
  title: "Why Betting & Gaming fits InfoBoxx",
  intent:
    "Users already search for how to win Aviator, best betting apps, and today’s predictions — meet them with education first.",
  education:
    "Guides, comparisons, tutorials, and responsible gambling framing build trust before any affiliate or sponsor mention.",
  conversion:
    "Affiliate partnerships, sponsored operators, SMS tips, app installs, and qualified leads — all work better after clarity.",
};

export const BETTING_GAMING_RESPONSIBLE = {
  title: "Responsible gambling & compliance",
  body:
    "InfoBoxx highlights licensed, regulated pathways and age-appropriate messaging. Gambling can be addictive — set limits, take breaks, and seek help if play stops feeling like entertainment. References to Ghana’s Gaming Commission and 18+-only participation reinforce long-term credibility.",
};

export const BETTING_GAMING_POPULAR_QUESTIONS = [
  {
    question: "Which betting site is best in Ghana?",
    slug: "best-betting-platforms",
  },
  {
    question: "Best online casino in Ghana?",
    slug: "casino-games",
  },
  {
    question: "How does Aviator work — and is it fair?",
    slug: "best-betting-platforms",
  },
  {
    question: "How do virtual sports betting games work?",
    slug: "virtual-games",
  },
  {
    question: "Which betting apps support MTN MoMo?",
    slug: "mobile-betting-apps",
  },
  {
    question: "What is rollover on a welcome bonus?",
    slug: "bonuses-promotions",
  },
  {
    question: "Is online betting legal in Ghana?",
    slug: "responsible-gambling",
  },
];
